import axiosInstant from '../../../api';
import { getSignedRequestEndpoint, postProfileEndpoint } from '../../../api/endpoints';

const bucket = 'desirelist-site-videos';

export const fetchData = async (url, options) => {
  try {
    const request = await fetch(url, options);
    const data = await request.json();

    if (request.ok) {
      return data;
    } else {
      return {
        error: data,
      };
    }
  } catch (e) {
    return {
      error: e.message || null,
    };
  }
};

// S3 signed-URL PUT. A non-2xx (expired signature, wrong bucket policy)
// used to resolve like a success and the caller then saved a URL that
// pointed at nothing — so reject on it.
export const putFile = async (url, file) => {
  const response = await fetch(`${url}`, { method: 'PUT', body: file });
  if (!response.ok) {
    throw new Error(`UploadFailed:${response.status}`);
  }
  return response;
};

// How long an upload may go without a single progress event before we give
// up on it. fetch() has no upload progress and no way to abort a stalled
// PUT, which is how the confirmation form's Submit hung for minutes.
const UPLOAD_STALL_MS = 90000;

// Same PUT over XMLHttpRequest so the caller gets progress (0-100) and a
// stalled transfer aborts instead of hanging forever.
export const putFileWithProgress = (url, file, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let stallTimer = null;
    const clearStall = () => {
      if (stallTimer) clearTimeout(stallTimer);
      stallTimer = null;
    };
    const armStall = () => {
      clearStall();
      stallTimer = setTimeout(() => {
        xhr.abort();
        reject(new Error('UploadStalled'));
      }, UPLOAD_STALL_MS);
    };
    xhr.open('PUT', url);
    xhr.upload.onprogress = (event) => {
      armStall();
      if (event.lengthComputable && typeof onProgress === 'function') {
        onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
      }
    };
    xhr.onload = () => {
      clearStall();
      if (xhr.status >= 200 && xhr.status < 300) {
        if (typeof onProgress === 'function') onProgress(100);
        resolve(xhr);
      } else {
        reject(new Error(`UploadFailed:${xhr.status}`));
      }
    };
    xhr.onerror = () => {
      clearStall();
      reject(new Error('UploadNetworkError'));
    };
    xhr.onabort = () => {
      clearStall();
      reject(new Error('UploadAborted'));
    };
    armStall();
    xhr.send(file);
  });

export const fetchSignedRequest = (name) => axiosInstant.get(getSignedRequestEndpoint(name, bucket));

const VIDEO_EXTENSIONS = { 'video/mp4': 'mp4', 'video/quicktime': 'mov', 'video/webm': 'webm' };

const videoExtension = (video) => {
  const type = ((video && video.type) || '').split(';')[0].toLowerCase();
  if (VIDEO_EXTENSIONS[type]) return VIDEO_EXTENSIONS[type];
  const fromName = ((video && video.name) || '').match(/\.(mp4|mov|webm|m4v)$/i);
  return fromName ? fromName[1].toLowerCase() : 'webm';
};

// Resolves to the public URL (or false on any failure — callers toast).
// Pass `onProgress` to get 0-100 upload progress; that path also aborts a
// stalled transfer instead of hanging.
export const uploadVideo = async (video, field, external, onProgress) => {
  try {
    // Name by the real container: uploads and Safari recordings are MP4/MOV,
    // and a hardcoded .webm mislabels them on download.
    const customName = `pro_recording_${field}_${Date.now()}.${videoExtension(video)}`;
    const { data } = await fetchSignedRequest(customName);
    const payload = new File([video], customName);
    if (typeof onProgress === 'function') {
      await putFileWithProgress(data.signedRequest, payload, onProgress);
    } else {
      await putFile(data.signedRequest, payload);
    }
    if (external) return data.url;
    if (data.url) {
      await axiosInstant.patch(postProfileEndpoint, { field, value: data.url });
      return data.url;
    }
  } catch (e) {
    console.error('[uploadVideo] failed:', (e && e.message) || e);
  }
  return false;
};
